export interface SettlementOrder {
  id: number;
  sellerAmount: number;
  sellerAmountWithFee: number;
  createdAt: string;
  packId: number | null;
  totalQuantity: number;
  pack: { id: number } | null;
  products: { id: number }[];
  payout?: { id: number; paidAt?: string | null; tossPayoutStatus?: string | null } | null;
  settle?: { paidAt?: string | null; state?: string | null } | null;
}

interface SettlementOrderTableProps {
  orders: SettlementOrder[];
  showPaidAt?: boolean;
}

function computeBreakdown(sellerAmount: number, sellerAmountWithFee: number) {
  const supplyPrice = Math.floor((sellerAmount * 10) / 11);
  const vat = sellerAmount - supplyPrice;
  const totalFee = sellerAmount - sellerAmountWithFee;
  const feeSupply = Math.floor((totalFee * 10) / 11);
  const feeVat = totalFee - feeSupply;
  return { supplyPrice, vat, totalFee, feeSupply, feeVat };
}

function getPackTypeLabel(order: SettlementOrder): string {
  if (order.packId) return "예약팩";
  if (order.products?.length > 0) return "실시간팩";
  return "";
}

function formatShortDate(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}`;
}

/**
 * 정산일 표시: 토스 Payout의 paidAt, 레거시 Settle의 paidAt 순으로 확인
 */
function resolvePaidAt(order: SettlementOrder): string {
  const raw = order.payout?.paidAt ?? order.settle?.paidAt ?? null;
  if (!raw) return "-";
  return formatShortDate(raw);
}

export default function SettlementOrderTable({
  orders,
  showPaidAt = false,
}: SettlementOrderTableProps) {
  return (
    <div className="mx-4 mt-4 mb-4 overflow-x-auto rounded-lg border border-gray-200 bg-white">
      <table className="min-w-[600px] w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200">
            <th className="px-3 py-3 text-left font-bold text-gray-700 whitespace-nowrap">주문일</th>
            <th className="px-3 py-3 text-left font-bold text-gray-700 whitespace-nowrap">상품금액(총액)</th>
            <th className="px-3 py-3 text-right font-bold text-gray-700 whitespace-nowrap">공급가액</th>
            <th className="px-3 py-3 text-right font-bold text-gray-700 whitespace-nowrap">VAT</th>
            <th className="px-3 py-3 text-right font-bold text-gray-700 whitespace-nowrap">수수료(vat포함)</th>
            <th className="px-3 py-3 text-right font-bold text-gray-700 whitespace-nowrap">실수령액</th>
            {showPaidAt && (
              <th className="px-3 py-3 text-right font-bold text-gray-700 whitespace-nowrap">정산일</th>
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {orders.map((order) => {
            const b = computeBreakdown(order.sellerAmount, order.sellerAmountWithFee);
            const packLabel = getPackTypeLabel(order);
            return (
              <tr key={order.id} className="hover:bg-gray-50">
                <td className="px-3 py-3 text-gray-700 whitespace-nowrap">
                  {formatShortDate(order.createdAt)}
                </td>
                <td className="px-3 py-3 text-gray-900 whitespace-nowrap">
                  {order.sellerAmount.toLocaleString()}원
                  {packLabel && (
                    <span
                      className={`ml-1 text-xs font-medium ${
                        packLabel === "예약팩" ? "text-green-600" : "text-blue-600"
                      }`}
                    >
                      ({packLabel})
                    </span>
                  )}
                </td>
                <td className="px-3 py-3 text-right text-gray-700 whitespace-nowrap">
                  {b.supplyPrice.toLocaleString()}원
                </td>
                <td className="px-3 py-3 text-right text-gray-700 whitespace-nowrap">
                  {b.vat.toLocaleString()}원
                </td>
                <td className="px-3 py-3 text-right text-gray-700 whitespace-nowrap">
                  {(b.feeSupply + b.feeVat).toLocaleString()}원
                </td>
                <td className="px-3 py-3 text-right font-semibold text-gray-900 whitespace-nowrap">
                  {order.sellerAmountWithFee.toLocaleString()}원
                </td>
                {showPaidAt && (
                  <td className="px-3 py-3 text-right text-green-700 font-semibold whitespace-nowrap">
                    {resolvePaidAt(order)}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
